import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UserPlus, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useI18n, ROLES } from "@/lib/i18n";

export default function AuditorCard({ audit }) {
  const { tx, lbl } = useI18n();
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [f, setF] = useState({ name: "", email: "", job_function: "" });
  const [link, setLink] = useState("");
  const load = useCallback(() => {
    api.get(`/audits/${audit.id}/members`).then((r) => setMembers(r.data.filter((m) => !m.revoked_at && ["lead", "consultant"].includes(m.role)))).catch(() => {});
    api.get(`/audits/${audit.id}/invitations`).then((r) => setPending(r.data.filter((i) => !i.accepted_at && !i.revoked_at))).catch(() => {});
  }, [audit.id]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try {
      const { data } = await api.post(`/audits/${audit.id}/invitations`, { ...f, role: "consultant" });
      setLink(data.link ? window.location.origin + data.link : "");
      toast.success(data.link ? tx("Auditeur invité : transmettez le lien", "Auditor invited: share the link") : tx("Invitation en attente d'approbation administrateur", "Invitation awaiting administrator approval"));
      setF({ name: "", email: "", job_function: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="card-surface p-5" data-testid="auditor-card">
      <div className="flex items-center gap-2 font-display text-lg font-semibold text-[#153A63]"><UserPlus className="h-5 w-5 text-[#2176AE]" />{tx("Ajouter un auditeur", "Add an auditor")}</div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <Field label={tx("Nom", "Name")}><Input data-testid="auditor-name" value={f.name} onChange={set("name")} /></Field>
        <Field label={tx("Fonction", "Function")}><Input data-testid="auditor-function" placeholder={tx("Consultant RH / Auditeur", "HR consultant / Auditor")} value={f.job_function} onChange={set("job_function")} /></Field>
        <div className="sm:col-span-2"><Field label={tx("E-mail professionnel (pour se connecter)", "Work email (to sign in)")}><Input type="email" data-testid="auditor-email" value={f.email} onChange={set("email")} /></Field></div>
      </div>
      <Button data-testid="auditor-add-btn" disabled={!f.name.trim() || !f.email.includes("@")} onClick={add} className="mt-3 bg-[#153A63]">{tx("Ajouter", "Add")}</Button>
      {link && <div className="mt-3 rounded border border-sky-200 bg-sky-50 p-2 text-xs break-all flex gap-2 items-start" data-testid="auditor-invite-link"><span className="flex-1">{link}</span><button data-testid="auditor-copy-link" onClick={() => { navigator.clipboard.writeText(link); toast.success(tx("Lien copié", "Link copied")); }}><Copy className="h-4 w-4" /></button></div>}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="auditor-list">
        {members.map((m) => (
          <span key={m.id} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs" data-testid={`auditor-chip-${m.user_id}`}>
            <b className="text-[#153A63]">{m.name}</b> — {m.role === "lead" ? tx("Auditeur principal (Responsable de mission)", "Lead auditor (Mission lead)") : (m.job_function || lbl(ROLES, m.role))}
          </span>
        ))}
        {pending.map((i) => <span key={i.id} className="rounded-full border border-dashed border-amber-300 bg-amber-50 px-3 py-1 text-xs text-amber-800" data-testid={`auditor-pending-${i.id}`}>{i.name || i.email} — {i.job_function || lbl(ROLES, i.audit_role)} · {tx("invitation en attente", "invitation pending")}</span>)}
      </div>
    </div>
  );
}
